/**
 * Pooled Borrow Dialog
 *
 * Dialog for executing pooled market (Aave-style) borrow transactions.
 * Shows transaction overview and uses DialogActionsPanel for execution.
 */

'use client'

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { invalidateBalancesCache } from '@/domain/cache'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useWrongNetwork } from '@/domain/hooks/useWrongNetwork'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { Objective } from '@/features/actions/logic/types'
import { DialogActionsPanel } from '@/features/dialogs/common/components/DialogActionsPanel'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { SuccessViewContent } from '@/features/dialogs/common/components/success-view/SuccessViewContent'
import { SuccessViewProceedButton } from '@/features/dialogs/common/components/success-view/SuccessViewProceedButton'
import { SuccessViewSummaryPanel } from '@/features/dialogs/common/components/success-view/SuccessViewSummaryPanel'
import { CommonDialogProps, DialogConfig } from '@/features/dialogs/common/types'
import { Dialog, DialogContent, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'

export interface PooledBorrowDialogProps extends CommonDialogProps {
  collateralToken: Token
  collateralAmount: NormalizedUnitNumber
  borrowToken: Token
  borrowAmount: NormalizedUnitNumber
  objectives: Objective[]
}

type DialogState = 'form' | 'success'

function PooledBorrowDialogContent({
  collateralToken,
  collateralAmount,
  borrowToken,
  borrowAmount,
  objectives,
  closeDialog,
}: Omit<PooledBorrowDialogProps, 'open' | 'setOpen'> & { closeDialog: () => void }) {
  const [state, setState] = useState<DialogState>('form')
  const queryClient = useQueryClient()
  const { chainId } = usePageChainId()

  const hasCollateral = Number(collateralAmount) > 0
  const hasBorrow = Number(borrowAmount) > 0

  const handleFinish = () => {
    // Invalidate balance caches to refresh wallet and position data
    invalidateBalancesCache({
      queryClient,
      chainId,
    })
    setState('success')
  }

  if (state === 'success') {
    return (
      <SuccessViewContent>
        <SuccessViewCheckmark isModal />

        <div className="flex w-full flex-col gap-3">
          {/* Collateral Panel */}
          {hasCollateral && (
            <SuccessViewSummaryPanel title="Deposited">
              <div className="flex items-center gap-2">
                <TokenIcon token={collateralToken} className="h-6" />
                <span className="font-semibold">{collateralToken.symbol}</span>
              </div>
              <div className="flex grow flex-col">
                <Typography className="text-right font-primary">
                  {collateralToken.format(collateralAmount, { style: 'auto' })}
                </Typography>
              </div>
            </SuccessViewSummaryPanel>
          )}

          {/* Borrow Panel */}
          {hasBorrow && (
            <SuccessViewSummaryPanel title="Borrowed">
              <div className="flex items-center gap-2">
                <TokenIcon token={borrowToken} className="h-6" />
                <span className="font-semibold">{borrowToken.symbol}</span>
              </div>
              <div className="flex grow flex-col">
                <Typography className="text-right font-primary">
                  {borrowToken.format(borrowAmount, { style: 'auto' })}
                </Typography>
              </div>
            </SuccessViewSummaryPanel>
          )}
        </div>

        <SuccessViewProceedButton onProceed={closeDialog}>Done</SuccessViewProceedButton>
      </SuccessViewContent>
    )
  }

  return (
    <MultiPanelDialog>
      <DialogTitle>
        {hasCollateral && hasBorrow ? 'Open Position' : hasBorrow ? 'Borrow' : 'Deposit Collateral'}
      </DialogTitle>

      {/* Transaction Overview */}
      <DialogPanel className="gap-4">
        <Typography variant="h4" className="text-primary">
          Transaction overview
        </Typography>
        <div className="flex flex-col gap-3">
          {/* Collateral */}
          {hasCollateral && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TokenIcon token={collateralToken} className="h-6" />
                <span className="text-muted-foreground text-sm">Deposit</span>
              </div>
              <div className="text-right">
                <Typography variant="p" className="font-semibold">
                  {collateralToken.format(collateralAmount, { style: 'auto' })} {collateralToken.symbol}
                </Typography>
              </div>
            </div>
          )}

          {/* Borrow */}
          {hasBorrow && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TokenIcon token={borrowToken} className="h-6" />
                <span className="text-muted-foreground text-sm">Borrow</span>
              </div>
              <div className="text-right">
                <Typography variant="p" className="font-semibold">
                  {borrowToken.format(borrowAmount, { style: 'auto' })} {borrowToken.symbol}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </DialogPanel>

      {/* Actions */}
      <DialogActionsPanel
        objectives={objectives}
        onFinish={handleFinish}
        enabled={objectives.length > 0}
      />
    </MultiPanelDialog>
  )
}

function PooledBorrowDialog({
  open,
  setOpen,
  collateralToken,
  collateralAmount,
  borrowToken,
  borrowAmount,
  objectives,
}: PooledBorrowDialogProps) {
  const isWrongNetwork = useWrongNetwork()

  if (isWrongNetwork) {
    setOpen(false)
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined}>
        <PooledBorrowDialogContent
          collateralToken={collateralToken}
          collateralAmount={collateralAmount}
          borrowToken={borrowToken}
          borrowAmount={borrowAmount}
          objectives={objectives}
          closeDialog={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

export const pooledBorrowDialogConfig: DialogConfig<PooledBorrowDialogProps> = {
  options: {
    closeOnChainChange: true,
  },
  element: PooledBorrowDialog,
}

export default PooledBorrowDialog
